<template>
    <AcademyLayout>
        <main class="container-fluid">
            <div class="courses-management-container">
                <div class="header-section">
                    <h2>{{ $t('academy.course-creation') }}</h2>
                    <b-button variant="primary" @click="showCreateModal = true">
                        <b-icon-plus-circle class="mr-2"></b-icon-plus-circle>
                        {{ $t('academy.create-new-course') }}
                    </b-button>
                </div>

                <!-- Filtros -->
                <div class="filters-section mb-4">
                    <b-row>
                        <b-col md="6">
                            <b-form-group :label="$t('academy.search-by-name')">
                                <b-form-input
                                    v-model="searchQuery"
                                    :placeholder="$t('academy.search-course-placeholder')"
                                    @input="filterCourses"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <!-- <b-col md="3">
                            <b-form-group label="Filtrar por estado">
                                <b-form-select
                                    v-model="selectedStatus"
                                    :options="statusOptions"
                                    @change="filterCourses"
                                ></b-form-select>
                            </b-form-group>
                        </b-col> -->
                        <!-- <b-col md="3">
                            <b-form-group label="Filtrar por modalidad">
                                <b-form-select
                                    v-model="selectedModality"
                                    :options="modalityOptions"
                                    @change="filterCourses"
                                ></b-form-select>
                            </b-form-group>
                        </b-col> -->
                    </b-row>
                </div>

                <!-- Tabla de Cursos -->
                <div class="table-responsive">
                    <b-table
                        :items="filteredCourses"
                        :fields="fields"
                        striped
                        hover
                        responsive
                        class="courses-table"
                    >
                        <template #cell(image)="data">
                            <div class="course-image">
                                <img 
                                    :src="data.item.image || noImage" 
                                    :alt="data.item.name"
                                    @error="handleImageError($event, data.item)"
                                />
                            </div>
                        </template>

                        <template #cell(name)="data">
                            <div class="d-flex flex-column">
                                <strong>{{ data.item.name }}</strong>
                                <small class="text-muted">{{ truncateText(data.item.description, 60) }}</small>
                            </div>
                        </template>

                        <template #cell(institution)="data">
                            <b-badge variant="primary">{{ data.item.institution }}</b-badge>
                        </template>

                        <template #cell(modules)="data">
                            <b-badge variant="info">{{ $tc('academy.number_modules',data.item.modules?.length || 0) }}</b-badge>
                        </template>

                        <template #cell(isRemote)="data">
                            <b-badge :variant="data.item.isRemote ? 'success' : 'warning'">
                                {{ data.item.isRemote ? $t('academy.remote') : $t('academy.in-person') }}
                            </b-badge>
                        </template>

                        <template #cell(block)="data">
                            <b-badge :variant="data.item.block ? 'danger' : 'success'">
                                {{ data.item.block ? $t('academy.locked') : $t('academy.active') }}
                            </b-badge>
                        </template>

                        <template #cell(actions)="data">
                            <div class="action-buttons">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="editCourse(data.item)"
                                    class="mr-2"
                                    v-b-tooltip.hover
                                    :title="$t('academy.edit-course')"
                                >
                                    <b-icon-pencil></b-icon-pencil>
                                </b-button>
                                <b-button
                                    variant="outline-info"
                                    size="sm"
                                    @click="manageModules(data.item)"
                                    class="mr-2"
                                    v-b-tooltip.hover
                                    :title="$t('academy.manage-modules')"
                                >
                                    <b-icon-collection></b-icon-collection>
                                </b-button>
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    @click="confirmDelete(data.item)"
                                    v-b-tooltip.hover
                                    :title="$t('academy.delete-course')"
                                >
                                    <b-icon-trash></b-icon-trash>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Modal para Crear/Editar Curso -->
                <b-modal
                    v-model="showCreateModal"
                    :title="isEditing ? $t('academy.edit-course') :  $t('academy.create-new-course')"
                    @ok="handleSaveCourse"
                    @hidden="resetForm"
                    size="xl"
                    :ok-title="$t('academy.save')"
                    :cancel-title="$t('academy.cancel')"
                >
                    <b-form @submit.stop.prevent="handleSaveCourse">
                        <b-row>
                            <b-col md="8">
                                <b-form-group :label="$t('academy.course-name-label')+' *'">
                                    <b-form-input
                                        v-model="courseForm.name"
                                        required
                                        :placeholder="$t('academy.course-name-placeholder')"
                                    ></b-form-input>
                                </b-form-group>

                                <b-form-group :label="$t('academy.description')+ ' *'">
                                    <b-form-textarea
                                        v-model="courseForm.description"
                                        required
                                        rows="3"
                                        :placeholder="$t('academy.course-description-placeholder')"
                                    ></b-form-textarea>
                                </b-form-group>

                                <b-form-group :label="$t('academy.institution')+ ' *'">
                                    <b-form-select
                                        v-model="courseForm.institution"
                                        :options="institutions.map(i => ({ value: i.name, text: i.name }))"
                                        required
                                        :placeholder="$t('academy.select_institution')"
                                    />
                                </b-form-group>

                                <b-row>
                                    <!-- <b-col md="6">
                                        <b-form-group>
                                            <label>Modalidad</label>
                                            <b-form-checkbox v-model="courseForm.isRemote">
                                                Curso remoto
                                            </b-form-checkbox>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group>
                                            <label>Estado</label>
                                            <b-form-checkbox v-model="courseForm.block">
                                                Bloquear curso
                                            </b-form-checkbox>
                                        </b-form-group>
                                    </b-col> -->
                                </b-row>
                            </b-col>
                            <b-col md="4">
                                <b-form-group :label="$t('academy.course-banner')">
                                    <div class="image-upload-section">
                                        <div class="current-image" v-if="courseForm.image">
                                            <img :src="courseForm.image" :alt="$t('academy.current-banner')" />
                                        </div>
                                        <b-form-file
                                            v-model="selectedFile"
                                            :placeholder="$t('academy.select-image')+ '...'"
                                            accept="image/*"
                                            @change="handleFileUpload"
                                        ></b-form-file>
                                        <small class="text-muted">{{ $t('academy.image-formats') }}</small>
                                    </div>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <hr>

                        <!-- Configuración de Módulos/Lecciones -->
                        <div class="modules-configuration">
                            <h5>{{ $t('academy.module-settings') }}</h5>
                            
                            <div class="add-module-section mb-3">
                                <b-button 
                                    variant="outline-success" 
                                    size="sm" 
                                    @click="addModule"
                                >
                                    <b-icon-plus></b-icon-plus> {{ $t('academy.add-module') }}
                                </b-button>
                            </div>

                            <div class="modules-list">
                                <div 
                                    v-for="(module, moduleIndex) in courseForm.modules" 
                                    :key="moduleIndex"
                                    class="module-item"
                                >
                                    <div class="module-header">
                                        <b-row>
                                            <b-col md="6">
                                                <b-form-group :label="`${$t('academy.module')} ${module.number} - ${$t('academy.name')} `">
                                                    <b-form-input
                                                        v-model="module.alias"
                                                        :placeholder="$t('academy.module-name')"
                                                    ></b-form-input>
                                                </b-form-group>
                                            </b-col>
                                            <b-col md="1" class="d-flex align-items-end">
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    @click="removeModule(moduleIndex)"
                                                >
                                                    <b-icon-trash></b-icon-trash>
                                                </b-button>
                                            </b-col>
                                        </b-row>
                                    </div>

                                    <b-form-group :label="$t('academy.module-description')">
                                        <b-form-textarea
                                            v-model="module.description"
                                            rows="2"
                                            :placeholder="$t('academy.module-description')"
                                        ></b-form-textarea>
                                    </b-form-group>

                                    <div class="activities-section">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{{ $t('academy.activities') }}</strong>
                                            <b-button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                @click="addActivity(moduleIndex)"
                                            >
                                                <b-icon-plus></b-icon-plus> {{ $t('academy.add-activity') }}
                                            </b-button>
                                        </div>

                                        <div 
                                            v-for="(activity, activityIndex) in module.lessons[0]?.classActivities || []" 
                                            :key="activityIndex"
                                            class="activity-item-expanded"
                                        >
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.activity-name') + '*'">
                                                        <b-form-input
                                                            v-model="activity.name"
                                                            :placeholder="$t('academy.activity-name')"
                                                            required
                                                        ></b-form-input>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            <!-- initial-description-placeholder -->
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.initial-description')+' *'">
                                                        <b-form-textarea
                                                            v-model="activity.InitialProjectDescription"
                                                            :placeholder="$t('academy.initial-description')"
                                                            rows="3"
                                                            required
                                                        ></b-form-textarea>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.activity-intro') +' *'">
                                                        <b-form-textarea
                                                            v-model="activity.introductionText"
                                                            :placeholder="$t('academy.activity-intro-placeholder')"
                                                            rows="4"
                                                            required
                                                        ></b-form-textarea>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            
                                            <div class="d-flex justify-content-end">
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    @click="removeActivity(moduleIndex, activityIndex)"
                                                >
                                                    <b-icon-trash></b-icon-trash> {{ $t('academy.delete') }}
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-form>
                </b-modal>

                <!-- Modal para Gestionar Módulos -->
                <b-modal
                    v-model="showModulesModal"
                    :title="$t('academy.manage-course-modules')"
                    size="xl"
                    hide-footer
                >
                    <div class="modules-management" v-if="currentCourse">
                        <div class="course-info mb-4">
                            <h5>{{ currentCourse.name }}</h5>
                            <p class="text-muted">{{ currentCourse.description }}</p>
                        </div>

                        <div class="modules-detailed-list">
                            <div 
                                v-for="(module, index) in currentCourse.modules" 
                                :key="index"
                                class="module-detail-card"
                            >
                                <b-card>
                                    <div class="module-detail-header">
                                        <h6>Módulo {{ module.number }}: {{ module.name }}</h6>
                                        <div class="module-actions">
                                            <b-button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                @click="editModule(index)"
                                            >
                                                <b-icon-pencil></b-icon-pencil>
                                            </b-button>
                                            <b-button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                @click="confirmDeleteModule(index)"
                                            >
                                                <b-icon-trash></b-icon-trash>
                                            </b-button>
                                        </div>
                                    </div>
                                    
                                    <p><strong>{{ $t('academy.description') }}:</strong> {{ module.description }}</p>
                                    
                                    <div class="activities-summary">
                                        <strong>{{ $t('academy.activities') }} ({{ module.lessons[0]?.classActivities?.length || 0 }}):</strong>
                                        <ul v-if="module.lessons[0]?.classActivities?.length">
                                            <li v-for="activity in module.lessons[0].classActivities" :key="activity.id">
                                                {{ activity.name }}
                                            </li>
                                        </ul>
                                        <p v-else class="text-muted">{{ $t('academy.no-activities') }}</p>
                                    </div>
                                </b-card>
                            </div>
                        </div>
                    </div>
                </b-modal>
            </div>
        </main>
    </AcademyLayout>
</template>

<script>
import AcademyLayout from '@/layouts/AcademyLayout.vue';
import coursesData from '@/data/coursesData';
import educationalInstitutionsData from '@/data/educationalInstitutionsData';
import { 
    BIconPlusCircle, 
    BIconPencil, 
    BIconTrash, 
    BIconCollection,
    BIconPlus
} from 'bootstrap-vue';
import Swal from 'sweetalert2';
import noImage from '@/assets/svg/no-image.svg';


export default {
    name: 'CourseManagementView',
    components: {
        AcademyLayout,
        BIconPlusCircle,
        BIconPencil,
        BIconTrash,
        BIconCollection,
        BIconPlus
    },
    data() {
        return {
            noImage,
            courses: [],
            filteredCourses: [],
            searchQuery: '',
            selectedStatus: null,
            selectedModality: null,
            showCreateModal: false,
            showModulesModal: false,
            isEditing: false,
            currentCourse: null,
            selectedFile: null,
            institutions: [],
            courseForm: {
                name: '',
                description: '',
                image: '',
                isRemote: true,
                block: false,
                institution: '',
                modules: []
            },
          /*   fields: [
                { key: 'image', label: 'Banner', thClass: 'text-center', tdClass: 'text-center' },
                { key: 'name', label: 'Nombre' },
                { key: 'institution', label: 'Institución', thClass: 'text-center', tdClass: 'text-center' },
                { key: 'modules', label: 'Módulos', thClass: 'text-center', tdClass: 'text-center' },
                { key: 'actions', label: 'Acciones', thClass: 'text-center', tdClass: 'text-center' }
            ], */
            statusOptions: [
                { value: null, text: 'Todos los estados' },
                { value: false, text: 'Activos' },
                { value: true, text: 'Bloqueados' }
            ],
            modalityOptions: [
                { value: null, text: 'Todas las modalidades' },
                { value: true, text: 'Remoto' },
                { value: false, text: 'Presencial' }
            ],
        };
    },
    computed: {
        fields() {
            return [
                { key: 'image', label: 'Banner', thClass: 'text-center', tdClass: 'text-center' },
                { key: 'name', label: this.$t('academy.name') },
                { key: 'institution', label: 'Institución', thClass: 'text-center', tdClass: 'text-center' },
                { key: 'modules', label: this.$t('academy.modules'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'actions', label: this.$t('academy.actions'), thClass: 'text-center', tdClass: 'text-center' }
            ];
        }
    },
    async mounted() {
        await this.loadCourses();
        await this.loadInstitutions();
    },
    methods: {
        async loadCourses() {
            try {
                this.courses = await coursesData.getAllCourses();
                this.filteredCourses = [...this.courses];
            } catch (error) {
                console.error('Error al cargar cursos:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.load-failed-courses')
                });
            }
        },
        async loadInstitutions() {
            try {
                this.institutions = await educationalInstitutionsData.getAllInstitutions();
            } catch (error) {
                this.institutions = [];
            }
        },
        filterCourses() {
            this.filteredCourses = this.courses.filter(course => {
                const matchesSearch = course.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                                    course.description.toLowerCase().includes(this.searchQuery.toLowerCase());
                const matchesStatus = this.selectedStatus === null || course.block === this.selectedStatus;
                const matchesModality = this.selectedModality === null || course.isRemote === this.selectedModality;
                return matchesSearch && matchesStatus && matchesModality;
            });
        },
        editCourse(course) {
            this.isEditing = true;
            this.currentCourse = course;
            this.courseForm = {
                name: course.name,
                description: course.description,
                image: course.image || '',
                isRemote: course.isRemote,
                block: course.block,
                institution: course.institution || '',
                modules: JSON.parse(JSON.stringify(course.modules || []))
            };
            this.showCreateModal = true;
        },
        async handleSaveCourse() {
            try {
                const courseData = {
                    name: this.courseForm.name,
                    description: this.courseForm.description,
                    image: this.courseForm.image,
                    isRemote: this.courseForm.isRemote,
                    block: this.courseForm.block,
                    institution: this.courseForm.institution,
                    modules: this.courseForm.modules
                };

                if (this.isEditing) {
                    await coursesData.updateCourse(this.currentCourse.id, courseData);
                } else {
                    await coursesData.createCourse(courseData);
                }

                await this.loadCourses();
                this.showCreateModal = false;
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.isEditing ? this.$t('academy.course-updated') : 'Curso creado correctamente'
                });
            } catch (error) {
                console.error('Error al guardar curso:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.course-save-failed')
                });
            }
        },
        async confirmDelete(course) {
            const result = await Swal.fire({
                title: this.$t('academy.are-you-sure'),
                text: this.$t('academy.delete-warning'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: this.$t('academy.confirm_delete'),
                cancelButtonText: this.$t('academy.cancel')
            });

            if (result.isConfirmed) {
                try {
                    await coursesData.deleteCourse(course.id);
                    await this.loadCourses();
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('academy.deleted'),
                        text: this.$t('academy.course-deleted')
                    });
                } catch (error) {
                    console.error('Error al eliminar curso:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.course-delete-failed')
                    });
                }
            }
        },
        manageModules(course) {
            this.currentCourse = course;
            this.showModulesModal = true;
        },
        addModule() {
            const moduleNumber = (this.courseForm.modules.length + 1).toString();
            const newModule = {
                name: '',
                description: '',
                number: moduleNumber,
                image: '',
                lessons: [{
                    classActivities: []
                }]
            };
            this.courseForm.modules.push(newModule);
        },
        removeModule(moduleIndex) {
            this.courseForm.modules.splice(moduleIndex, 1);
            // Renumerar módulos
            this.courseForm.modules.forEach((module, index) => {
                module.number = (index + 1).toString();
            });
        },
        addActivity(moduleIndex) {
            if (!this.courseForm.modules[moduleIndex].lessons[0].classActivities) {
                this.courseForm.modules[moduleIndex].lessons[0].classActivities = [];
            }
            
            const activityId = `activity${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
            const newActivity = {
                id: activityId,
                idStart: `${activityId}Start`,
                name: '',
                InitialProjectDescription: '',
                introductionText: '',
                code: "#CODIGO DEL PROYECTO",
                componentsDiagram: this.$t('academy.activity-link'),
                stepByStepGuide: this.$t('academy.pending')
            };
            
            this.courseForm.modules[moduleIndex].lessons[0].classActivities.push(newActivity);
        },
        removeActivity(moduleIndex, activityIndex) {
            this.courseForm.modules[moduleIndex].lessons[0].classActivities.splice(activityIndex, 1);
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validaciones básicas
            if (file.size > 2 * 1024 * 1024) { // 2MB
                Swal.fire({
                    icon: 'error',
                    title: this.$t('academy.file-too-large'),
                    text: this.$t('academy.file-size-limit')
                });
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: this.$t('academy.invalid-format'),
                    text: this.$t('academy.allowed-formats')
                });
                return;
            }

            // Aquí deberías implementar la subida a Firebase Storage
            // Por ahora, creamos una URL temporal para preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.courseForm.image = e.target.result;
            };
            reader.readAsDataURL(file);

            // TODO: Implementar subida real a Firebase Storage
            // const imageUrl = await uploadImageToFirebase(file);
            // this.courseForm.image = imageUrl;
        },
        handleImageError(event, course) {
            console.warn(`Error cargando imagen para curso: ${course.name}`);
            event.target.src = this.noImage;
        },
        truncateText(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        },
        async confirmDeleteModule(moduleIndex) {
            const result = await Swal.fire({
                title: this.$t('academy.are-you-sure'),
                text: this.$t('academy.delete-module-warning'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: this.$t('academy.confirm_delete'),
                cancelButtonText: this.$t('academy.cancel')
            });

            if (result.isConfirmed) {
                try {
                    await coursesData.removeModuleFromCourse(this.currentCourse.id, moduleIndex);
                    await this.loadCourses();
                    // Actualizar el curso actual
                    this.currentCourse = this.courses.find(c => c.id === this.currentCourse.id);
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('academy.deleted'),
                        text: this.$t('academy.module-deleted')
                    });
                } catch (error) {
                    console.error('Error al eliminar módulo:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.module-delete-failed')
                    });
                }
            }
        },
        editModule(moduleIndex) {
            // Cerrar modal de módulos y abrir modal de edición con el módulo seleccionado
            this.showModulesModal = false;
            this.editCourse(this.currentCourse);
        },
        resetForm() {
            this.isEditing = false;
            this.currentCourse = null;
            this.selectedFile = null;
            this.courseForm = {
                name: '',
                description: '',
                image: '',
                isRemote: true,
                block: false,
                institution: '',
                modules: []
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.courses-management-container {
    background: white;
    border-radius: 15px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
        margin: 0;
        color: #333;
    }
}

.filters-section {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.courses-table {
    margin-top: 20px;

    ::v-deep th {
        background-color: #f8f9fa;
        font-weight: 600;
    }
}

.course-image {
    img {
        width: 60px;
        height: 40px;
        object-fit: cover;
        border-radius: 5px;
        border: 1px solid #dee2e6;
    }
}

.action-buttons {
    display: flex;
    gap: 5px;
    justify-content: center;
}

.image-upload-section {
    .current-image {
        margin-bottom: 10px;
        
        img {
            width: 100%;
            max-height: 200px;
            object-fit: cover;
            border-radius: 8px;
            border: 1px solid #dee2e6;
        }
    }
}

.modules-configuration {
    .add-module-section {
        text-align: center;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 5px;
    }
}

.module-item {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
    background: #f8f9fa;

    .module-header {
        margin-bottom: 10px;
    }

    .activities-section {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #dee2e6;

        .activity-item {
            margin-bottom: 10px;
            padding: 8px;
            background: white;
            border-radius: 5px;
            border: 1px solid #e9ecef;
        }
    }
}

.modules-management {
    .course-info {
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
    }
}

.module-detail-card {
    margin-bottom: 15px;

    .module-detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        h6 {
            margin: 0;
            color: #333;
        }

        .module-actions {
            display: flex;
            gap: 5px;
        }
    }

    .activities-summary {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #e9ecef;

        ul {
            margin: 10px 0;
            padding-left: 20px;
        }
    }
}

::v-deep .modal-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

::v-deep .modal-footer {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
}

// Responsive adjustments
@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 3px;
    }
    
    .course-image img {
        width: 40px;
        height: 30px;
    }
}
</style>